import {createAsyncThunk, createEntityAdapter, createSlice} from "@reduxjs/toolkit";
import type {RootState} from "./store";
import type {Promotion, PromotionCreateRequest, PromotionUpdateRequest} from "../types/promotion";
import {
  createPromotion as createPromotionApi,
  deletePromotion as deletePromotionApi,
  getPromotions,
  updatePromotion as updatePromotionApi,
} from "../api/promotionsApi";

type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";

const promotionsAdapter = createEntityAdapter<Promotion>({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

export type PromotionsState = ReturnType<typeof promotionsAdapter.getInitialState> & {
  status: AsyncStatus;
  error: string | null;
  createStatus: AsyncStatus;
  updateStatus: AsyncStatus;
  deleteStatus: AsyncStatus;
};

const initialState: PromotionsState = promotionsAdapter.getInitialState({
  status: "idle",
  error: null,
  createStatus: "idle",
  updateStatus: "idle",
  deleteStatus: "idle",
});

export const fetchPromotions = createAsyncThunk<Promotion[], void>(
  "promotions/fetchPromotions",
  async () => getPromotions(),
);

export const createPromotion = createAsyncThunk<Promotion, PromotionCreateRequest>(
  "promotions/createPromotion",
  async (body) => createPromotionApi(body),
);

export const updatePromotion = createAsyncThunk<Promotion, PromotionUpdateRequest>(
  "promotions/updatePromotion",
  async (body) => updatePromotionApi(body.id, body),
);

export const deletePromotion = createAsyncThunk<string, string>(
  "promotions/deletePromotion",
  async (id) => {
    await deletePromotionApi(id);
    return id;
  },
);

export const promotionsSlice = createSlice({
  name: "promotions",
  initialState,
  reducers: {
    reset(state) {
      promotionsAdapter.removeAll(state);
      state.status = "idle";
      state.error = null;
      state.createStatus = "idle";
      state.updateStatus = "idle";
      state.deleteStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPromotions.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPromotions.fulfilled, (state, action) => {
        state.status = "succeeded";
        promotionsAdapter.setAll(state, action.payload);
      })
      .addCase(fetchPromotions.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load promotions";
      })
      .addCase(createPromotion.pending, (state) => {
        state.createStatus = "loading";
        state.error = null;
      })
      .addCase(createPromotion.fulfilled, (state, action) => {
        state.createStatus = "succeeded";
        promotionsAdapter.addOne(state, action.payload);
      })
      .addCase(createPromotion.rejected, (state, action) => {
        state.createStatus = "failed";
        state.error = action.error.message ?? "Failed to create promotion";
      })
      .addCase(updatePromotion.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(updatePromotion.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";
        promotionsAdapter.updateOne(state, {id: action.payload.id, changes: action.payload});
      })
      .addCase(updatePromotion.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error = action.error.message ?? "Failed to update promotion";
      })
      .addCase(deletePromotion.pending, (state) => {
        state.deleteStatus = "loading";
        state.error = null;
      })
      .addCase(deletePromotion.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded";
        promotionsAdapter.removeOne(state, action.payload);
      })
      .addCase(deletePromotion.rejected, (state, action) => {
        state.deleteStatus = "failed";
        state.error = action.error.message ?? "Failed to delete promotion";
      });
  },
});

export const {reset} = promotionsSlice.actions;
export const promotionsSelectors = promotionsAdapter.getSelectors<RootState>((s) => s.promotions);
